/**
 * @graphql cc
 */
import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import { calculateEstimatedReadTime, getAdf, getWordCount } from '../readTimeUtils.ts';
import { extractContentFeatures, extractContentFeaturesSkipExpand } from '../featuresExtractor.ts';
import { ReadTimeQuery } from '../ReadTimeQuery.graphql.ts';
import type {
	ReadTimeQuery as ReadTimeQueryType,
	ReadTimeQueryVariables,
} from '../__types__/ReadTimeQuery.ts';

export const useReadTime = (contentId: string, skipQuery?: boolean) => {
	const readTimeQueryOptions = {
		variables: {
			contentId,
		},
		skip: skipQuery,
	};

	// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
	const { data, loading, error } = expValEquals(
		'confluence_frontend_stop_watching_readtimequery',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<ReadTimeQueryType, ReadTimeQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
				ReadTimeQuery,
				readTimeQueryOptions,
			)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQuery<ReadTimeQueryType, ReadTimeQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
				ReadTimeQuery,
				{
					...readTimeQueryOptions,
					fetchPolicy: 'cache-only',
				},
			);

	const { adf, readTime, wordCount, features, featuresSkipExpand } = useMemo(() => {
		const adfFromData = getAdf(data ?? undefined);

		if (!adfFromData || loading || error || !contentId) {
			return {
				readTime: null,
				wordCount: null,
				features: null,
				featuresSkipExpand: null,
			};
		}

		return {
			adf: adfFromData,
			readTime: calculateEstimatedReadTime(adfFromData),
			wordCount: getWordCount(adfFromData),
			features: extractContentFeatures(adfFromData),
			featuresSkipExpand: extractContentFeaturesSkipExpand(adfFromData),
		};
	}, [loading, error, data, contentId]);

	const contentNode = data?.content?.nodes?.[0];
	const contentType = contentNode?.type;

	return {
		loading,
		error,
		adf,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
	};
};
